import preventDefault
  from '@mailobj-browser/front/js/events/hooks/preventDefault.js'
import stopImmediatePropagation
  from '@mailobj-browser/front/js/events/hooks/stopImmediatePropagation.js'
import {
  tapUp
} from '@mailobj-browser/front/js/events/listeners/builtins/tap.js'
import listener from '@mailobj-browser/front/js/events/listeners/listener.js'
import capture from '@mailobj-browser/front/js/events/options/capture.js'
import once from '@mailobj-browser/front/js/events/options/once.js'
import passive from '@mailobj-browser/front/js/events/options/passive.js'
import contextMenu from '@mailobj-browser/front/js/events/types/contextMenu.js'
import keyUp from '@mailobj-browser/front/js/events/types/keyUp.js'
import mouseMove from '@mailobj-browser/front/js/events/types/mouseMove.js'
import mouseOver from '@mailobj-browser/front/js/events/types/mouseOver.js'
import resize from '@mailobj-browser/front/js/events/types/resize.js'
import scroll from '@mailobj-browser/front/js/events/types/scroll.js'
import touchMove from '@mailobj-browser/front/js/events/types/touchMove.js'
import remove from '@mailobj-browser/front/js/tree/remove.js'
import array from '@mailobj-browser/front/js/utils/array.js'
import object from '@mailobj-browser/front/js/utils/object.js'
import resolvable from '@mailobj-browser/front/js/utils/resolvable.js'
import globals from '#utilities/js/ea/globals.js'
import rect from '../ea/rect.js'
import * as fixed from '../fixed/fixed.js'
import * as lightbox from './lightbox.js'

const openers = new WeakMap()
let current = null
let position = null

export const { events, focus, open, opener, parse } = lightbox

export const close = () => {
  if (current && !globalThis.preventClose) {
    if (typeof globals.ea_timeout_refresh_unpause === 'function') {
      globals.ea_timeout_refresh_unpause()
    }

    const { ownerDocument } = current
    const { defaultView } = ownerDocument
    const { CustomEvent } = defaultView
    const opener = openers.get(current)

    onOpenerTapUp.forget(opener)
    remove(current)
    current = null

    opener.dispatchEvent(new CustomEvent(events.close, {
      bubbles: true
    }))
  }
}

export const hasCurrent = () => {
  return current !== null &&
    current.ownerDocument.contains(current)
}

export const content = element => {
  if (current && current.contains(element)) {
    return current
  }
}

export const isOpen = () => {
  return current && current.ownerDocument.contains(current)
}

const onContextMenu = object(listener, {
  type: contextMenu,
  passive,
  task: close
})

const onOpenerTapUp = object(tapUp, {
  hooks: array([
    preventDefault,
    stopImmediatePropagation
  ]),
  capture,
  once,
  task: () => {
    close()
  }
})

const onResize = object(listener, {
  capture,
  once,
  passive,
  type: resize,
  task: close
})

const onScroll = object(listener, {
  type: scroll,
  capture,
  passive,
  task (document, { target }) {
    if (!scrolls(current, position, target)) {
      this.forget(document)
      close()
    }
  }
})

export const onKeyUp = object(listener, {
  type: keyUp,
  capture,
  passive,
  task: async (element, event) => {
    const { key } = event

    if (key === 'Escape' && hasCurrent()) {
      const focusable = opener(current)

      close()
      focusable.focus()
    }
  }
})

const onMove = object(listener, {
  capture,
  passive,
  task: async (document, event) => {
    const { touches: [touch] = [] } = event
    const { clientX, clientY } = touch ?? event

    position = object(null, { clientX, clientY })
  }
})

export const onMouseMove = object(onMove, {
  type: mouseMove
})

export const onMouseOver = object(onMove, {
  type: mouseOver
})

export const onTouchMove = object(onMove, {
  type: touchMove
})

export const display = async (content, opener, event = null) => {
  const { ownerDocument } = opener
  const { defaultView } = ownerDocument
  const { events, onEscape } = lightbox
  const { CustomEvent } = defaultView
  const { fromEvent, fromNode, move, resize, bottomLeft } = fixed
  const [promise, { resolve }] = resolvable()

  move(content)

  requestAnimationFrame(() => {
    if (event) {
      move(content, fromEvent(content, event))
    } else {
      resize(content, opener, bottomLeft)
      move(content, fromNode(content, opener))
      openers.set(content, opener)
    }

    onEscape.listen(ownerDocument)
    onMouseMove.listen(ownerDocument)
    onMouseOver.listen(ownerDocument)
    onTouchMove.listen(ownerDocument)
    onScroll.listen(ownerDocument)
    onResize.listen(defaultView)
    onContextMenu.listen(content)
    onOpenerTapUp.listen(opener)
    current = content
    resolve(content)
    opener.dispatchEvent(new CustomEvent(events.open, {
      detail: content
    }))
  })

  return promise
}

const scrolls = (
  current,
  position,
  target
) => {
  if (!current || !position) {
    return false
  }

  if (current === target || current.contains(target)) {
    return true
  }

  const { bottom, left, right, top } = rect(current)
  const { clientX, clientY } = position

  return clientY >= top &&
    clientY <= bottom &&
    clientX >= left &&
    clientX <= right
}
